import { useCallback, useEffect, useState } from 'react';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import {
  dismissOrganizationLogoNudge,
  isOrganizationLogoNudgeDismissed,
} from './onboardingUiStorage';

export function useOrganizationLogoNudge(userId: string | undefined, organizationId: string | undefined) {
  const { entitlements } = usePlanEntitlements();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const canEditOrgPermission = useModulePermission('Organization', 'edit');
  const isOwner = entitlements?.is_owner === true;
  const canManageIdentity = isOwner || canEditOrgPermission;

  const hasLogo = Boolean(businessProfile?.organization_logo_url?.trim());

  const [cardVisible, setCardVisible] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!userId || !organizationId || !canManageIdentity || hasLogo) {
      setCardVisible(false);
      setCardReady(true);
      return () => {
        cancelled = true;
      };
    }

    setCardReady(false);
    void (async () => {
      const dismissed = await isOrganizationLogoNudgeDismissed(userId, organizationId);
      if (cancelled) return;
      setCardVisible(!dismissed);
      setCardReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, organizationId, canManageIdentity, hasLogo]);

  const dismissCard = useCallback(async () => {
    if (!userId || !organizationId) return;
    setDismissing(true);
    try {
      await dismissOrganizationLogoNudge(userId, organizationId);
      setCardVisible(false);
    } finally {
      setDismissing(false);
    }
  }, [userId, organizationId]);

  const showMarkHint = canManageIdentity && !hasLogo;
  const showNudgeCard = cardReady && cardVisible && canManageIdentity && !hasLogo;

  return {
    canManageIdentity,
    hasLogo,
    showMarkHint,
    showNudgeCard,
    dismissCard,
    dismissing,
  };
}
